import React, { useState } from "react";
import {
  RotateCcw,
} from "lucide-react";
import toast from "react-hot-toast";
import { ApiError } from "../../store/utils/apiError";
import dayjs, { Dayjs } from "dayjs";
import { useAuth } from "../../store/hooks/useAuth";
import { useAppSelector } from "../../store/hooks";
import type { RootState } from "../../store";
import { isWeekOffDate } from "../../utils/weekOff";
import { ConfirmationModal, Modal, DatePicker, Button, PageHeader, Select } from "../common";
import TabSelector from "./component/TabSelector"
import {
  COMP_OFF_PENDING_STATUSES,
  COMP_OFF_APPROVED_STATUSES,
  COMP_OFF_REJECTED_STATUSES,
  getCompOffStatusColor,
  isPendingCompOffStatus,
} from "../../utils/constants";
import type { CompOffItem } from "../../store/apis/compOff.api";
import { useHasPermission } from "../../store/hooks/useRbac";
import { PERMISSIONS } from "../../utils/rbac/permissions";
import CompensatoryLeaveTable, {
  type CompOffStatusFilter,
} from "./component/CompensatoryLeaveTable";

export enum ServiceTabKey {
  NOTIFICATION = "notification",
  COMP_OFF = "comp-off",
  GAME_BOOKING = "game-booking",
  DOCUMENT_APPROVALS = "document-approvals",
}
import { TextArea } from "../common/TextArea";
import { getWorkingPeriodLabel } from "../../utils/compOff";
import {
  useGetCompOffListQuery,
  useCreateCompOffMutation,
  usePullBackCompOffMutation,
} from "../../store/apis/compOff.api";
import GameBookingTab from "./component/GameBookingTab";
import { getCompOffStatusIcon } from "../../utils/compOff";
import { formatDateDDMMM } from "../../utils/timeUtils";

const LeaveCompOff: React.FC = () => {
  const { user } = useAuth();
  const weekOffConfig = useAppSelector(
    (state: RootState) => state.auth.weekOffConfig,
  );
  const userTimezone = dayjs.tz.guess();
  const canManage = useHasPermission(PERMISSIONS.EMPLOYEE_SELF_SERVICE_MANAGE);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTarget, setConfirmTarget] = useState<CompOffItem | null>(null);
  const [confirmLoading, setConfirmLoading] = useState(false);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<CompOffItem | null>(
    null,
  );
  const [active, setActive] = useState<ServiceTabKey>(ServiceTabKey.COMP_OFF);
  const [statusFilter, setStatusFilter] =
    useState<CompOffStatusFilter>("all");
  const [formData, setFormData] = useState({
    leaveDate: "",
    comment: "",
    isFirstHalf: false,
    isSecondHalf: false,
  });

  const listParams = {
    userId: user?.id ?? "",
    ...(statusFilter !== "all"
      ? {
        status:
          statusFilter === "pending"
            ? COMP_OFF_PENDING_STATUSES
            : statusFilter === "approved"
              ? COMP_OFF_APPROVED_STATUSES
              : COMP_OFF_REJECTED_STATUSES,
      }
      : {}),
  };
  const {
    data: listResponse,
    isLoading: historyLoading,
    refetch: refetchList,
  } = useGetCompOffListQuery(listParams, {
    skip: !user?.id,
  });
  const [createCompOff, { isLoading: submitting }] = useCreateCompOffMutation();
  const [pullBackCompOff, { isLoading: pullBackLoading }] =
    usePullBackCompOffMutation();

  const compOffRequests = listResponse?.data ?? [];

  const mainTabs: { k: ServiceTabKey; label: string }[] = [
    { k: ServiceTabKey.COMP_OFF, label: "Comp-Off Request" },
    { k: ServiceTabKey.GAME_BOOKING, label: "Game Booking" },
  ];

  const showErrorToast = (message: string) => toast.error(message);
  const showSuccessToast = (message: string) => toast.success(message);

  const handleTabChange = (tab: ServiceTabKey) => {
    if (tab === ServiceTabKey.COMP_OFF) refetchList();
    setActive(tab);
  };

  const handleStatusFilterChange = (value: CompOffStatusFilter) => {
    setStatusFilter(value);
  };

  const onPullbackClick = (req: CompOffItem) => {
    setConfirmTarget(req);
    setConfirmOpen(true);
  };

  const canPullback = (req: CompOffItem): boolean => {
    if (!isPendingCompOffStatus(req.status)) return false;
    const todayStr = new Date().toISOString().split("T")[0];
    const leaveDateStr =
      typeof req.leaveDate === "string" ? req.leaveDate.split("T")[0] : "";
    return leaveDateStr > todayStr;
  };

  const confirmPullback = async () => {
    if (!user?.id || !confirmTarget?.id) return;
    try {
      setConfirmLoading(true);
      await pullBackCompOff({
        id: confirmTarget.id,
        userId: user.id,
      }).unwrap();
      showSuccessToast("Comp-off pulled back successfully.");
      setConfirmOpen(false);
      setConfirmTarget(null);
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage =
        err?.data?.message || err?.message || "Failed to pull back.";
      const errorMessage = Array.isArray(rawMessage)
        ? rawMessage.join(", ")
        : rawMessage;
      showErrorToast(errorMessage);
    } finally {
      setConfirmLoading(false);
    }
  };

  const handleViewDetails = (request: CompOffItem) => {
    setSelectedRequest(request);
    setDetailsModalOpen(true);
  };

  const handleDateChange = (value: Dayjs | null) => {
    const newDate = value ? value.format("YYYY-MM-DD") : "";

    if (value) {
      if (!isWeekOffDate(value.toDate(), weekOffConfig)) {
        showErrorToast(
          "Comp-Off can only be requested for configured week-off days or holidays.",
        );

        setFormData((prev) => ({ ...prev, leaveDate: "" }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      leaveDate: newDate,
    }));
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleReset = () => {
    setFormData({
      leaveDate: "",
      comment: "",
      isFirstHalf: false,
      isSecondHalf: false,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user?.id) {
      showErrorToast("User not found. Please try again.");
      return;
    }
    if (!formData.leaveDate) {
      showErrorToast("Please select a leave date.");
      return;
    }
    if (!formData.isFirstHalf && !formData.isSecondHalf) {
      showErrorToast("Please select at least one working period.");
      return;
    }
    if (formData.leaveDate && dayjs(formData.leaveDate).isAfter(dayjs())) {
      showErrorToast("You cannot select a future date.");
      return;
    }
    const commentValue = formData.comment ? formData.comment.trim() : "";
    const commentRegex = /^(?=.*[a-zA-Z]).{10,}$/;
    if (!commentRegex.test(commentValue)) {
      showErrorToast(
        "Comments must be at least 10 characters long and contain at least one letter.",
      );
      return;
    }
    try {
      const response = await createCompOff({
        userIds: [user.id],
        leaveDate: formData.leaveDate,
        comment: formData.comment,
        isFirstHalf: formData.isFirstHalf,
        isSecondHalf: formData.isSecondHalf,
      }).unwrap();
      const results = response?.results ?? [];
      const skipped = response?.skipped ?? [];
      if (skipped.length > 0) {
        showErrorToast(`Request already exists for: ${skipped.join(", ")}`);
      }
      if (results.length > 0) {
        showSuccessToast("Leave request submitted successfully!");
        setFormData({
          leaveDate: "",
          comment: "",
          isFirstHalf: false,
          isSecondHalf: false,
        });
      }
    } catch (error: unknown) {
      const err = error as ApiError;
      const rawMessage =
        err?.data?.message ||
        "Failed to submit leave request. Please try again.";
      const errorMessage = Array.isArray(rawMessage)
        ? rawMessage.join(", ")
        : rawMessage;
      showErrorToast(errorMessage);
    }
  };

  const getStatusLabel = (request: CompOffItem) =>
    request.statusLabel ?? request.status;

  return (
    <>
      <div>
        <div className="max-w-7xl mx-auto space-y-4">
          <PageHeader
            className="!mb-2 sm:!mb-3"
            title="Employee Self Service"
            description="Comp-off requests and game room bookings"
            breadcrumbs={[{ label: "Home" }, { label: "Self-Service" }]}
          />

          <TabSelector
            tabs={mainTabs}
            active={active}
            onChange={handleTabChange}
            className="mb-5"
          />
        </div>
      </div>
      <div className="max-w-7xl mx-auto">
        {active === ServiceTabKey.COMP_OFF &&
          (canManage ? (
            <div className="bg-white rounded-[9px] border border-border-secondary shadow-xs mt-3 mb-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4 px-4 sm:px-[22px] py-3.5 sm:py-[18px] border-b border-border-tertiary">
                <div className="text-[15px] sm:text-heading-sm font-bold text-text-primary">
                  Comp-Off Request
                </div>
              </div>
              <form className="space-y-4 p-6">
                <div className="grid grid-cols-1 gap-6">
                  <div className="flex flex-col lg:flex-row lg:items-start gap-8">
                    <div className="w-full lg:w-[480px] shrink-0">
                      <DatePicker
                        label="Select Date"
                        required
                        value={
                          formData.leaveDate ? dayjs(formData.leaveDate) : null
                        }
                        onChange={handleDateChange}
                        placeholder="Select date"
                        className="w-full"
                        format="DD/MM/YYYY"
                        allowClear={false}
                        maxDate={dayjs().endOf("day")}
                        placement="bottomLeft"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs font-semibold text-[var(--color-text-primary)] mb-[7px]">
                        Working Period
                        <span className="text-[var(--color-error)] ml-1">*</span>
                      </label>
                      <div className="flex items-center gap-4 h-[44px]">
                        <label className="flex items-center cursor-pointer select-none">
                          <input
                            type="checkbox"
                            name="isFirstHalf"
                            checked={formData.isFirstHalf}
                            onChange={handleInputChange}
                            className="w-5 h-5 appearance-none rounded-sm border border-border-input bg-white checked:bg-primary-600 checked:border-transparent cursor-pointer transition-all relative after:content-[''] after:absolute after:hidden checked:after:block after:left-1.5 after:top-1 after:w-1.5 after:h-2.5 after:border-white after:border-r-2 after:border-b-2 after:rotate-45"
                          />
                          <span className="ml-2 text-sm text-slate-800">
                            1st Half
                          </span>
                        </label>
                        <label className="flex items-center cursor-pointer select-none">
                          <input
                            type="checkbox"
                            name="isSecondHalf"
                            checked={formData.isSecondHalf}
                            onChange={handleInputChange}
                            className="w-5 h-5 appearance-none rounded-sm border border-border-input bg-white checked:bg-primary-600 checked:border-transparent cursor-pointer transition-all relative after:content-[''] after:absolute after:hidden checked:after:block after:left-1.5 after:top-1 after:w-1.5 after:h-2.5 after:border-white after:border-r-2 after:border-b-2 after:rotate-45"
                          />
                          <span className="ml-2 text-sm text-slate-800">
                            2nd Half
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <TextArea
                    label="Comment"
                    value={formData.comment}
                    onChange={(value) =>
                      setFormData({ ...formData, comment: value })
                    }
                    placeholder="Please provide your reason & timesheet data here..."
                    required
                    minRows={6}
                    minLength={10}
                  />
                </div>

                <div className="flex flex-row items-center gap-3 max-xxs:gap-2 justify-end">
                  <Button
                    htmlType="button"
                    appearance="secondary"
                    size="middle"
                    className="max-xxs:!px-3 max-xxs:!py-1.5 max-xxs:!text-xs max-xxs:!h-auto max-xxs:!rounded-md"
                    onClick={handleReset}
                    icon={<RotateCcw className="w-4 h-4 max-xxs:w-3.5 max-xxs:h-3.5" />}
                  >
                    Reset
                  </Button>
                  <Button
                    onClick={handleSubmit}
                    appearance="primary"
                    size="middle"
                    className="max-xxs:!px-3 max-xxs:!py-1.5 max-xxs:!text-xs max-xxs:!h-auto max-xxs:!rounded-md !bg-gradient-to-r !from-btn-brand-start !to-btn-brand-end !text-white hover:!shadow-btn-action hover:!translate-y-[-1px] transition-all"
                    loading={submitting}
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </form>
            </div>
          ) : (
            <div className="p-6 bg-white rounded-xl shadow-soft border border-slate-200 mt-3 text-center mb-6">
              <p className="text-md text-slate-600">
                You do not have permission to create Comp-Off Requests.
              </p>
            </div>
          ))}

        {active === ServiceTabKey.GAME_BOOKING && <GameBookingTab />}

        {active === ServiceTabKey.COMP_OFF && (
          <CompensatoryLeaveTable
            title="Comp-Off Request History"
            requests={compOffRequests}
            loading={historyLoading}
            onViewDetails={handleViewDetails}
            onPullback={canManage ? onPullbackClick : () => { }}
            canPullback={canManage ? canPullback : () => false}
            getStatusLabel={getStatusLabel}
            userTimezone={userTimezone}
            statusFilter={statusFilter}
            onStatusFilterChange={handleStatusFilterChange}
          />
        )}

        <Modal
          isOpen={detailsModalOpen}
          onClose={() => setDetailsModalOpen(false)}
          title="Comp-Off Details"
          size="md"
        >
          {selectedRequest && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-slate-900">
                <div>
                  <p className="font-medium text-slate-600">Leave Date</p>
                  <p>{formatDateDDMMM(selectedRequest.leaveDate, userTimezone)}</p>
                </div>

                <div>
                  <p className="font-medium text-slate-600">Working Period</p>
                  <p>
                    <span className="text-sm text-slate-900">
                      {getWorkingPeriodLabel(
                        selectedRequest.isFirstHalf,
                        selectedRequest.isSecondHalf,
                      )}
                    </span>
                  </p>
                </div>

                <div>
                  <p className="font-medium text-slate-600">Applied On</p>
                  <p>{formatDateDDMMM(selectedRequest.createdAt, userTimezone)}</p>
                </div>
                <div>
                  <p className="font-medium text-slate-600">Status</p>
                  <div
                    className={`flex w-fit px-3 py-1 items-center rounded-full whitespace-nowrap border ${getCompOffStatusColor(selectedRequest.status)}`}
                  >
                    <span className="flex-shrink-0">
                      {getCompOffStatusIcon(selectedRequest.status)}
                    </span>

                    <span className="ml-2 text-xs font-semibold capitalize">
                      {selectedRequest.statusLabel ?? selectedRequest.status}
                    </span>
                  </div>
                </div>
              </div>
              <div>
                <p className="font-medium text-slate-600 ">Comment</p>
                <p className="text-sm text-slate-900 whitespace-pre-wrap">
                  {selectedRequest.comment}
                </p>
              </div>

              {selectedRequest.rejectionReason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="font-medium text-red-700 text-sm">
                    Rejection Reason
                  </p>
                  <p className="text-sm text-red-700 mt-1">
                    {selectedRequest.rejectionReason}
                  </p>
                </div>
              )}
            </div>
          )}
        </Modal>

        <ConfirmationModal
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          onConfirm={confirmPullback}
          title="Pull back comp-off request"
          message={
            confirmTarget
              ? `Are you sure you want to pull back your comp-off request?`
              : ""
          }
          type="warning"
          confirmText={
            confirmLoading || pullBackLoading ? "Pulling back..." : "Pull back"
          }
          isLoading={confirmLoading || pullBackLoading}
        />
      </div>
    </>
  );
};

export default LeaveCompOff;
